import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAgent } from 'agents/react';

import type {
  ActionResultWithIds,
  AgentStateMessage,
  DataActionResultMessage,
  GruAgentState,
  GruWebSocketMessage,
  WorkflowAppliedMessage,
  WorkflowGroup,
} from './types';

import { ENV } from 'services/environment/environment';
import { clearAgent } from 'services/gru/gruApi';
import { logError } from 'services/logging/logging';

import { buildAgentName } from './helpers';

type UseGruAgentOptions = {
  messageId: string;
  partnerId: string;
  userName: string;
  userLocale: string;
  partnerName: string;
  isAdmin: boolean;
};

const CONNECTION_TIMEOUT_MS = 10_000;

export const useGruAgent = ({
  messageId,
  partnerId,
  userName,
  userLocale,
  partnerName,
  isAdmin,
}: UseGruAgentOptions) => {
  const [connected, setConnected] = useState(false);
  const [connectionTimedOut, setConnectionTimedOut] = useState(false);
  const [agentState, setAgentState] = useState<GruAgentState>({
    workflowGroups: [],
  });
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hasReceivedMessage = useRef(false);

  useEffect(() => {
    if (connected) {
      clearTimeout(timeoutRef.current);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      setConnectionTimedOut(true);
    }, CONNECTION_TIMEOUT_MS);

    return () => clearTimeout(timeoutRef.current);
  }, [connected]);

  const query = useMemo(
    () => ({ messageId, partnerId, userName, userLocale, partnerName }),
    [messageId, partnerId, userName, userLocale, partnerName],
  );

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const raw = JSON.parse(event.data as string);
      const msg = raw as GruWebSocketMessage;

      if (!hasReceivedMessage.current) {
        hasReceivedMessage.current = true;
        setConnected(true);
      }

      switch (msg.type) {
        case 'agent-state':
          setAgentState(parseAgentStateMessage(msg));
          break;
        case 'data-action-result':
          setAgentState((prev) => handleDataActionResult(prev, msg));
          break;
        case 'workflow-applied':
          setAgentState((prev) => handleWorkflowApplied(prev, msg));
          break;
        default:
          break;
      }
    } catch {
      // Non-JSON WS messages (e.g. pings) are expected and safely ignored
    }
  }, []);

  const agentName = buildAgentName(messageId, partnerId, isAdmin);

  useAgent({
    agent: 'ChatAgent',
    name: agentName,
    host: ENV.GRU_HOST,
    query,
    onClose: useCallback(() => {
      setConnected(false);
      hasReceivedMessage.current = false;
    }, []),
    onMessage: handleMessage,
    onError: useCallback((error: Event) => {
      logError(error);
    }, []),
  });

  const resetState = useCallback(() => {
    setAgentState({ workflowGroups: [] });
  }, []);

  const clear = useCallback(() => {
    resetState();
    clearAgent(agentName).catch(logError);
  }, [agentName, resetState]);

  return {
    workflowGroups: agentState.workflowGroups,
    connected,
    connectionTimedOut,
    resetState,
    agentName,
    clear,
  };
};

const parseAgentStateMessage = (msg: AgentStateMessage): GruAgentState => {
  const workflowGroups: Array<WorkflowGroup> = msg.workflows.map(({ workflow, actionResults }) => {
    const parsed: Array<ActionResultWithIds> = actionResults
      .filter((ar) => ar.payload && typeof ar.payload === 'object' && 'action' in ar.payload)
      .map((ar) => ({
        ...ar.payload,
        visible: typeof ar.visible === 'number' ? ar.visible === 1 : Boolean(ar.visible),
        id: ar.id,
      }));

    return { workflow, actionResults: parsed };
  });

  workflowGroups.sort((a, b) => {
    if (a.workflow.status === 'running') {
      return 1;
    }
    if (b.workflow.status === 'running') {
      return -1;
    }
    return 0;
  });

  return { workflowGroups };
};

const handleDataActionResult = (prev: GruAgentState, msg: DataActionResultMessage): GruAgentState => {
  const groupIndex = prev.workflowGroups.findIndex((g) => g.workflow.id === msg.data.workflow.id);

  if (groupIndex === -1) {
    return {
      workflowGroups: [...prev.workflowGroups, { workflow: msg.data.workflow, actionResults: [msg.data] }],
    };
  }

  const group = prev.workflowGroups[groupIndex];
  const existingIndex = group.actionResults.findIndex((ar) => ar.id === msg.data.id);
  const actionResults =
    existingIndex !== -1
      ? group.actionResults.map((ar, i) => (i === existingIndex ? msg.data : ar))
      : [...group.actionResults, msg.data];

  const updatedGroups = prev.workflowGroups.map((g, i) =>
    i === groupIndex ? { workflow: msg.data.workflow, actionResults } : g,
  );

  return { workflowGroups: updatedGroups };
};

const handleWorkflowApplied = (prev: GruAgentState, msg: WorkflowAppliedMessage): GruAgentState => ({
  workflowGroups: prev.workflowGroups.map((g) =>
    g.workflow.id === msg.workflowId
      ? { ...g, workflow: { ...g.workflow, status: 'applied' as const, applied_at: msg.appliedAt } }
      : g,
  ),
});
